import Keycloak, {type KeycloakLoginOptions} from 'keycloak-js';
import {getMe} from '../api/userApi';
import type {User} from '../entities/User';
import {appendQueryParamToUrl} from '../util/urlUtils';

/**
 * Documentation of the Keycloak JS adapter:
 * https://www.keycloak.org/securing-apps/javascript-adapter
 */
let keycloak: Keycloak | undefined;

/** The logged in user of the backend, set by {@link prepare} and updated when the profile is saved. */
export const session: {currentUser?: User} = {};

/** Keycloak "application initiated action": the user changes the password on the Keycloak page. */
export const UPDATE_PASSWORD_ACTION = 'UPDATE_PASSWORD';

export type ActionStatus = 'success' | 'cancelled' | 'error';

export interface ActionResult {
  status: ActionStatus;
  /** The action, e.g. {@link UPDATE_PASSWORD_ACTION}; undefined if Keycloak did not send it. */
  action?: string;
}

/** Result of the action Keycloak just redirected back from, until a page took it with {@link takeActionResult}. */
let actionResult: ActionResult | undefined;

/**
 * Creates the adapter for the given Keycloak client, checks for an existing SSO session and loads the
 * current user. Must be called once before the app is rendered.
 */
export async function prepare(clientId: string): Promise<void> {
  keycloak = new Keycloak(`/api/keycloak_config?clientId=${encodeURIComponent(clientId)}`);
  keycloak.onActionUpdate = (status, action) => {
    actionResult = {status, action};
  };
  const authenticated = await keycloak.init({
    onLoad: 'check-sso',
    checkLoginIframe: false,
    enableLogging: import.meta.env.DEV,
  });
  if (authenticated) {
    session.currentUser = await getMe();
  }
}

export function getKeycloak(): Keycloak {
  if (!keycloak) {
    throw new Error('Keycloak has not been initialized yet');
  }
  return keycloak;
}

export function isAuthenticated(): boolean {
  return keycloak?.authenticated === true;
}

/** Redirects to the Keycloak login page. The returned promise only settles if the redirect fails. */
export function login(options?: KeycloakLoginOptions): Promise<void> {
  return getKeycloak().login(options).catch((error: unknown) => {
    console.error(`Keycloak login failed: ${String(error)}`);
    alert('Die Anmeldung konnte nicht gestartet werden.');
    throw error;
  });
}

export function logout(): Promise<void> {
  return getKeycloak().logout();
}

/**
 * Redirects to the Keycloak page for changing the password (which may ask for the current password first) and
 * returns to `redirectUri` afterwards, where {@link takeActionResult} tells whether the password was changed.
 */
export function changePassword(redirectUri: string): Promise<void> {
  return login({action: UPDATE_PASSWORD_ACTION, redirectUri});
}

/**
 * Returns the result of the Keycloak action the app was just redirected back from (e.g. {@link changePassword}),
 * or `undefined` if there is none. Each result is returned only once.
 */
export function takeActionResult(): ActionResult | undefined {
  const result = actionResult;
  actionResult = undefined;
  return result;
}

/**
 * Returns a valid access token for the backend (refreshing it if it expires within the next seconds),
 * or `undefined` if the user is not logged in.
 */
export async function getAccessToken(): Promise<string | undefined> {
  if (!keycloak?.authenticated) {
    return undefined;
  }
  try {
    await keycloak.updateToken(5);
  } catch (error) {
    window.alert('Ihre Sitzung ist abgelaufen, Sie werden abgemeldet.');
    keycloak.clearToken();
    throw error;
  }
  return keycloak.token;
}

export function appendAccessTokenToUrl(url: string | null | undefined): string | undefined {
  return appendQueryParamToUrl(url, 'access_token', keycloak?.token ?? '');
}
